// Polar Night motion (DESIGN.md "Motion"). One module for every lane:
//   - tokens mirrored from styles/signal.css (--sg-fast … --sg-stagger) for JS,
//   - usePresence: keep a CSS-animated layer mounted while it plays its exit,
//   - framer-motion presets (dialog, menu, sheet, toast, moment, fade) that
//     collapse to instant opacity under prefers-reduced-motion,
//   - the spring for sliding indicators (segmented thumb, tab underline),
//   - staggerDelay for first-paint card grids (never table rows).
// CSS keyframes and the data-state="closing" rules live in signal.css.

import { useCallback, useEffect, useState } from "react";
import { useReducedMotion } from "framer-motion";

/** Durations in milliseconds; keep in step with the --sg-* tokens. */
export const MS = Object.freeze({
  micro: 120, // --sg-fast: hover, press, colour
  small: 180, // --sg-mid: menus, popovers, tooltips
  moment: 240, // --sg-slow: dialogs, session moments, toasts
  large: 360, // --sg-large: sheets and route-level changes
  exit: 120, // --sg-exit: menus, popovers, tooltips leaving
  exitSlow: 140, // --sg-exit-slow: dialogs, palette, sheets, toasts leaving
  indicator: 200, // --sg-indicator: sliding thumbs and underlines
  stagger: 24, // --sg-stagger: per-item delay on first paint
});

/** Cubic-bezier control points; keep in step with the CSS easings. */
export const EASE = Object.freeze({
  standard: [0.2, 0.8, 0.2, 1], // --sg-ease: enter and state changes
  exit: [0.4, 0, 1, 1], // --sg-ease-exit: anything leaving
  spring: [0.32, 1.32, 0.56, 1], // --sg-ease-spring: press release, settle
});

/** Physical springs for framer-motion: indicators and drag settle. */
export const SPRING = Object.freeze({
  indicator: { type: "spring", stiffness: 560, damping: 42, mass: 0.9 },
  settle: { type: "spring", stiffness: 420, damping: 32, mass: 1 },
});

/** First-paint stagger caps at the sixth item so long grids never wait. */
export const STAGGER_MAX_ITEMS = 6;

const seconds = (ms) => ms / 1000;

/**
 * Delay in ms for the item at `index` in a first-paint stagger. Items past
 * the cap share the last slot. Returns 0 when motion is reduced.
 */
export const staggerDelay = (
  index,
  { step = MS.stagger, max = STAGGER_MAX_ITEMS, reduced = false } = {},
) => {
  if (reduced) return 0;
  const i = Math.max(0, Math.floor(Number(index) || 0));
  return Math.min(i, max - 1) * step;
};

/** True when the environment can play CSS/Web animations (jsdom cannot). */
const canAnimate = () =>
  typeof window !== "undefined" &&
  typeof window.Element !== "undefined" &&
  typeof window.Element.prototype.getAnimations === "function";

/**
 * prefers-reduced-motion as a boolean (framer's hook returns null before it
 * has read the media query; treat that as "no preference").
 */
export const usePrefersReducedMotion = () => Boolean(useReducedMotion());

/**
 * Keep a layer mounted while it plays its exit animation.
 *
 *   const presence = usePresence(open, { exitMs: MS.exitSlow });
 *   if (!presence.mounted) return null;
 *   <div className="sg-dialog" data-state={presence.state}
 *        onAnimationEnd={presence.onAnimationEnd}>…</div>
 *
 * `state` is "open" or "closing"; signal.css animates [data-state="closing"]
 * for .sg-dialog, .sg-palette, .sg-menu, .sg-scrim, .sg-toast and friends.
 * Unmounts on the layer's own animationend, or after exitMs as a fallback.
 * Reduced motion, or an environment that cannot animate, unmounts at once.
 */
export const usePresence = (open, { exitMs = MS.exit } = {}) => {
  const reduced = usePrefersReducedMotion();
  const [phase, setPhase] = useState(open ? "open" : "unmounted");

  // Derived state, set during render so open and close never lag a frame.
  if (open && phase !== "open") setPhase("open");
  if (!open && phase === "open") {
    setPhase(reduced || exitMs <= 0 || !canAnimate() ? "unmounted" : "closing");
  }

  useEffect(() => {
    if (phase !== "closing") return undefined;
    const timer = setTimeout(() => setPhase("unmounted"), exitMs + 60);
    return () => clearTimeout(timer);
  }, [phase, exitMs]);

  const onAnimationEnd = useCallback((event) => {
    if (event && event.target !== event.currentTarget) return;
    setPhase((current) => (current === "closing" ? "unmounted" : current));
  }, []);

  return {
    mounted: phase !== "unmounted",
    state: phase === "closing" ? "closing" : "open",
    isClosing: phase === "closing",
    onAnimationEnd,
  };
};

/* ── framer-motion presets ─────────────────────────────────────────────── */

const enter = (ms) => ({ duration: seconds(ms), ease: EASE.standard });
const leave = (ms) => ({ duration: seconds(ms), ease: EASE.exit });

/** Variants keyed hidden / visible / exit. */
export const PRESETS = Object.freeze({
  // Dialog and palette: 6 px rise, .98 scale in; .98 scale out.
  dialog: {
    hidden: { opacity: 0, y: 6, scale: 0.98 },
    visible: { opacity: 1, y: 0, scale: 1, transition: enter(MS.moment) },
    exit: { opacity: 0, y: 0, scale: 0.98, transition: leave(MS.exitSlow) },
  },
  // Menus, popovers, selects: 4 px drop from the trigger.
  menu: {
    hidden: { opacity: 0, y: -4, scale: 0.98 },
    visible: { opacity: 1, y: 0, scale: 1, transition: enter(MS.small) },
    exit: { opacity: 0, y: -4, scale: 0.98, transition: leave(MS.exit) },
  },
  // Right-edge sheets and drawers.
  sheet: {
    hidden: { opacity: 0, x: 24 },
    visible: { opacity: 1, x: 0, transition: enter(MS.large) },
    exit: { opacity: 0, x: 24, transition: leave(MS.exitSlow) },
  },
  // Toasts rise from below and leave to the right.
  toast: {
    hidden: { opacity: 0, y: 12, scale: 0.98 },
    visible: { opacity: 1, y: 0, scale: 1, transition: enter(MS.moment) },
    exit: { opacity: 0, x: 16, transition: leave(MS.exitSlow) },
  },
  // Session moments and route content: 6 px rise cross-fade.
  moment: {
    hidden: { opacity: 0, y: 6 },
    visible: { opacity: 1, y: 0, transition: enter(MS.moment) },
    exit: { opacity: 0, transition: leave(MS.exit) },
  },
  fade: {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: enter(MS.small) },
    exit: { opacity: 0, transition: leave(MS.exit) },
  },
});

const INSTANT = { duration: 0 };

/** Reduced motion: nothing travels or scales; layers appear and go at once. */
const REDUCED_VARIANTS = Object.freeze({
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: INSTANT },
  exit: { opacity: 0, transition: INSTANT },
});

/**
 * Props for a framer `motion.*` element from a preset, honouring reduced
 * motion. Spread onto the element inside <AnimatePresence>.
 *
 *   <AnimatePresence>{open && <motion.div {...motionPreset("menu", reduced)} />}</AnimatePresence>
 *
 * `delayMs` staggers first paint (use staggerDelay).
 */
export const motionPreset = (name, reduced = false, { delayMs = 0 } = {}) => {
  const base = PRESETS[name] || PRESETS.fade;
  if (reduced) {
    return {
      variants: REDUCED_VARIANTS,
      initial: "hidden",
      animate: "visible",
      exit: "exit",
    };
  }
  const variants = delayMs
    ? {
        ...base,
        visible: {
          ...base.visible,
          transition: { ...base.visible.transition, delay: seconds(delayMs) },
        },
      }
    : base;
  return { variants, initial: "hidden", animate: "visible", exit: "exit" };
};

/** Hook form of motionPreset that reads the user's motion preference. */
export const useMotionPreset = (name, options) => {
  const reduced = usePrefersReducedMotion();
  return motionPreset(name, reduced, options);
};

/** Transition for a shared-layout indicator (layoutId thumb or underline). */
export const indicatorTransition = (reduced = false) =>
  reduced ? INSTANT : SPRING.indicator;
